import React, { useState, useEffect } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISS_KEY = 'pwa_dismissed_at';
const DISMISS_WINDOW_MS = 24 * 3600 * 1000;

function rememberDismissal() {
  try {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  } catch {
    // localStorage unavailable (private mode / quota) — nothing to persist.
  }
}

export function PWAInstallBanner() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Don't show if already installed (standalone mode)
    if (window.matchMedia('(display-mode: standalone)').matches) return;
    // Don't show again within 24h of dismissal
    try {
      const dismissedAt = localStorage.getItem(DISMISS_KEY);
      if (dismissedAt && Date.now() - parseInt(dismissedAt, 10) < DISMISS_WINDOW_MS) return;
    } catch {
      // localStorage unavailable — proceed
    }

    function handler(e: Event) {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setVisible(true);
    }

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  function handleInstall() {
    if (!deferredPrompt) return;
    void deferredPrompt.prompt();
    void deferredPrompt.userChoice.then(({ outcome }) => {
      // Declining the OS sheet is a dismissal too — without recording it the
      // banner reappears on the next `beforeinstallprompt`.
      if (outcome === 'dismissed') rememberDismissal();
      setVisible(false);
      setDeferredPrompt(null);
    });
  }

  function handleDismiss() {
    rememberDismissal();
    setVisible(false);
  }

  if (!visible) return null;

  return (
    <div
      role="complementary"
      aria-label="Install app"
      // z-30 keeps this under the side drawer (z-40) and the burger button
      // (z-50); at z-50 it floated on top of the open drawer.
      className="fixed left-4 z-30 flex max-w-xs items-center gap-3 rounded-xl border border-indigo-200 bg-white px-4 py-3 shadow-lg dark:border-indigo-800 dark:bg-gray-800"
      style={{ bottom: 'calc(max(1rem, env(safe-area-inset-bottom)) + 3.5rem)' }}
    >
      <span aria-hidden="true" className="text-2xl">📲</span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">Install the app</p>
        <p className="text-xs text-gray-500 dark:text-gray-400">Play offline, faster launches</p>
      </div>
      <div className="flex flex-col gap-1">
        <button
          type="button"
          onClick={handleInstall}
          className="rounded-lg bg-indigo-600 px-3 py-1 text-xs font-semibold text-white transition-colors hover:bg-indigo-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
        >
          Install
        </button>
        <button
          type="button"
          onClick={handleDismiss}
          className="rounded text-xs text-gray-500 transition-colors hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:text-gray-400 dark:hover:text-gray-300"
        >
          Not now
        </button>
      </div>
    </div>
  );
}
